/**
 * The webhook endpoints editor (wave b12 frontend item 6; wave COM item 5), a
 * configuration panel over GET, POST and PATCH /api/configuration/webhook-endpoints.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * An endpoint is its url, the NAME of the environment variable that holds its signing
 * secret (`secret_ref`), the events it receives and whether it is active. The secret is
 * never entered or shown: the register shows the server's `secret_fingerprint` beside
 * the variable's name, so two secrets can be told apart.
 *
 * The refusals about the url (422 webhook.private_address, the Director's ruling 8 on
 * bundle 2, and webhook.url_refused, webhook.endpoint_exists) carry no field path, so the
 * dialog places the server's sentence under the url field itself and marks it; the ones
 * about the variable (webhook.secret_ref_format, webhook.secret_missing) go under the
 * variable's field. Any other refusal is said inside the dialog, as every dialog does.
 */

import { useState } from "react";

import { ApiError } from "../api/client";
import {
  WEBHOOK_EVENT_KINDS,
  createWebhookEndpoint,
  listWebhookEndpoints,
  updateWebhookEndpoint,
  type WebhookEndpoint,
  type WebhookEndpointPayload,
} from "../api/configuration";
import { useToken } from "../auth/sessionContext";
import { strings } from "../brand/strings";
import { DataTable, type RegisterColumn } from "@/components/ui/data-table";
import { ServerField } from "../lib/serverErrors";
import { ActionDialog, ToastBar } from "../nav/ActionDialog";
import { EmptyState, SkeletonRows, StateChip } from "./parts";
import { useQuery } from "./useQuery";

/** The refusals said under one field, by the field they are about. */
const URL_RULES: readonly string[] = [
  "webhook.private_address",
  "webhook.url_refused",
  "webhook.endpoint_exists",
];
const SECRET_RULES: readonly string[] = ["webhook.secret_ref_format", "webhook.secret_missing"];

function eventLabel(kind: string): string {
  return strings.webhookEvents[kind] ?? kind;
}

export function WebhookEndpointsPanel(): React.JSX.Element {
  const token = useToken();
  const copy = strings.configuration;
  const [stamp, setStamp] = useState(0);
  const [editing, setEditing] = useState<WebhookEndpoint | "new" | null>(null);
  const [toast, setToast] = useState<{ message: string; tone: "info" | "danger" } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const endpoints = useQuery((signal) => listWebhookEndpoints(token, signal), [token, stamp]);

  async function toggle(row: WebhookEndpoint): Promise<void> {
    setBusyId(row.id);
    try {
      await updateWebhookEndpoint(token, row.id, { active: !row.active });
      setToast({
        message: row.active ? copy.webhookDeactivated : copy.webhookActivated,
        tone: "info",
      });
      setStamp((one) => one + 1);
    } catch (caught) {
      setToast({
        message:
          caught instanceof ApiError && caught.problem?.detail
            ? caught.problem.detail
            : strings.common.failed,
        tone: "danger",
      });
    } finally {
      setBusyId(null);
    }
  }

  const columns = endpointColumns(
    (row) => {
      setEditing(row);
    },
    (row) => {
      void toggle(row);
    },
    busyId,
  );

  return (
    <div className="q-ui flex min-w-0 flex-col gap-3" data-testid="configuration-webhooks">
      {toast !== null && (
        <ToastBar
          message={toast.message}
          tone={toast.tone}
          onDone={() => {
            setToast(null);
          }}
        />
      )}
      <div className="q-actions-row">
        <button
          type="button"
          className="q-button q-mini--primary"
          data-testid="webhook-add"
          onClick={() => {
            setEditing("new");
          }}
        >
          {copy.webhookAdd}
        </button>
      </div>
      {endpoints.kind === "loading" && <SkeletonRows />}
      {(endpoints.kind === "failed" || endpoints.kind === "absence") && (
        <div className="q-refusal" role="alert">
          <p className="q-refusal__heading">{strings.common.failed}</p>
        </div>
      )}
      {endpoints.kind === "ready" &&
        (endpoints.data.length === 0 ? (
          <EmptyState title={copy.webhooksHeading} body={copy.webhooksEmpty} />
        ) : (
          <DataTable
            data={endpoints.data}
            columns={columns}
            caption={copy.webhooksHeading}
            testId="webhook-endpoints"
            getRowId={(row) => row.id}
            getRowTestId={() => "webhook-endpoint-row"}
          />
        ))}
      {editing !== null && (
        <EndpointDialog
          endpoint={editing === "new" ? null : editing}
          onClose={() => {
            setEditing(null);
          }}
          onSaved={(created) => {
            setEditing(null);
            setToast({ message: created ? copy.webhookCreated : copy.webhookSaved, tone: "info" });
            setStamp((one) => one + 1);
          }}
        />
      )}
    </div>
  );
}

function endpointColumns(
  onEdit: (row: WebhookEndpoint) => void,
  onToggle: (row: WebhookEndpoint) => void,
  busyId: string | null,
): RegisterColumn<WebhookEndpoint>[] {
  const copy = strings.configuration;
  return [
    {
      id: "url",
      label: copy.webhookUrlColumn,
      text: (row) => row.url,
      className: "font-mono break-all min-w-[16rem]",
    },
    {
      id: "events",
      label: copy.webhookEventsColumn,
      text: (row) => row.eventKinds.map(eventLabel).join("; "),
    },
    {
      id: "secret",
      label: copy.webhookSecretColumn,
      text: (row) => copy.webhookFingerprint(row.secretRef, row.secretFingerprint),
      render: (row) => (
        <span data-testid="webhook-secret">
          <span className="font-mono">{row.secretRef}</span>{" "}
          <span className="text-muted-foreground">({row.secretFingerprint})</span>
        </span>
      ),
    },
    {
      id: "state",
      label: copy.webhookStateColumn,
      text: (row) => (row.active ? copy.webhookActive : copy.webhookInactive),
      filter: true,
      render: (row) => (
        <StateChip
          state={row.active ? "CONFIRMED" : null}
          label={row.active ? copy.webhookActive : copy.webhookInactive}
        />
      ),
    },
    {
      id: "act",
      label: strings.nav.actions,
      text: () => copy.webhookEdit,
      render: (row) => (
        <span className="q-actions-row">
          <button
            type="button"
            className="q-button q-button--quiet"
            data-testid="webhook-edit"
            onClick={() => {
              onEdit(row);
            }}
          >
            {copy.webhookEdit}
          </button>
          <button
            type="button"
            className="q-button q-button--quiet"
            data-testid="webhook-toggle"
            disabled={busyId === row.id}
            onClick={() => {
              onToggle(row);
            }}
          >
            {row.active ? copy.webhookDeactivate : copy.webhookActivate}
          </button>
        </span>
      ),
    },
  ];
}

/** Add an endpoint, or change one (only the fields that changed are sent). */
function EndpointDialog({
  endpoint,
  onClose,
  onSaved,
}: {
  readonly endpoint: WebhookEndpoint | null;
  readonly onClose: () => void;
  readonly onSaved: (created: boolean) => void;
}): React.JSX.Element {
  const token = useToken();
  const copy = strings.configuration;
  const [url, setUrl] = useState(endpoint?.url ?? "");
  const [secretRef, setSecretRef] = useState(endpoint?.secretRef ?? "");
  const [events, setEvents] = useState<readonly string[]>(endpoint?.eventKinds ?? []);
  const [active, setActive] = useState(endpoint?.active ?? true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const rule = error?.problem?.rule ?? "";
  const urlWords = URL_RULES.includes(rule) ? (error?.problem?.detail ?? null) : null;
  const secretWords = SECRET_RULES.includes(rule) ? (error?.problem?.detail ?? null) : null;
  const placed = urlWords !== null || secretWords !== null;
  const ready = url.trim() !== "" && secretRef.trim() !== "" && events.length > 0;

  async function send(): Promise<void> {
    setBusy(true);
    setError(null);
    const full: WebhookEndpointPayload = {
      url: url.trim(),
      secret_ref: secretRef.trim(),
      event_kinds: WEBHOOK_EVENT_KINDS.filter((one) => events.includes(one)),
      active,
    };
    try {
      if (endpoint === null) {
        await createWebhookEndpoint(token, full);
      } else {
        const change: Partial<WebhookEndpointPayload> = {
          ...(full.url !== endpoint.url ? { url: full.url } : {}),
          ...(full.secret_ref !== endpoint.secretRef ? { secret_ref: full.secret_ref } : {}),
          ...(full.event_kinds.join() !== endpoint.eventKinds.join()
            ? { event_kinds: full.event_kinds }
            : {}),
          ...(full.active !== endpoint.active ? { active: full.active } : {}),
        };
        await updateWebhookEndpoint(token, endpoint.id, change);
      }
      onSaved(endpoint === null);
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <ActionDialog
      open
      wide
      title={endpoint === null ? copy.webhookCreateHeading : copy.webhookEditHeading(endpoint.url)}
      onClose={onClose}
      error={placed ? undefined : error}
    >
      <div className="q-field">
        <label className="q-field__label" htmlFor="webhook-url">
          {copy.webhookUrlLabel}
        </label>
        <p className="q-note" id="webhook-url-hint">
          {copy.webhookUrlHint}
        </p>
        <ServerField field="url">
          <input
            id="webhook-url"
            className="q-input"
            type="url"
            inputMode="url"
            data-testid="webhook-url"
            aria-describedby={urlWords === null ? "webhook-url-hint" : "webhook-url-refusal"}
            aria-invalid={urlWords !== null}
            value={url}
            onChange={(event) => {
              setUrl(event.target.value);
            }}
          />
        </ServerField>
        {urlWords !== null && (
          <p
            className="q-field__error"
            id="webhook-url-refusal"
            role="alert"
            data-testid="webhook-url-refusal"
          >
            {urlWords}
          </p>
        )}
      </div>
      <div className="q-field">
        <label className="q-field__label" htmlFor="webhook-secret-ref">
          {copy.webhookSecretLabel}
        </label>
        <p className="q-note" id="webhook-secret-ref-hint">
          {copy.webhookSecretHint}
        </p>
        <ServerField field="secret_ref">
          <input
            id="webhook-secret-ref"
            className="q-input font-mono"
            autoComplete="off"
            spellCheck={false}
            data-testid="webhook-secret-ref"
            aria-describedby={
              secretWords === null ? "webhook-secret-ref-hint" : "webhook-secret-ref-refusal"
            }
            aria-invalid={secretWords !== null}
            value={secretRef}
            onChange={(event) => {
              setSecretRef(event.target.value);
            }}
          />
        </ServerField>
        {secretWords !== null && (
          <p
            className="q-field__error"
            id="webhook-secret-ref-refusal"
            role="alert"
            data-testid="webhook-secret-ref-refusal"
          >
            {secretWords}
          </p>
        )}
        {endpoint !== null && (
          <p className="q-note" data-testid="webhook-fingerprint">
            {copy.webhookFingerprint(endpoint.secretRef, endpoint.secretFingerprint)}
          </p>
        )}
      </div>
      <ServerField field="event_kinds">
        <fieldset className="q-field" id="webhook-event-kinds" data-testid="webhook-event-kinds">
          <legend className="q-field__label">{copy.webhookEventsLabel}</legend>
          {WEBHOOK_EVENT_KINDS.map((kind) => (
            <label key={kind} className="q-check">
              <input
                type="checkbox"
                data-testid={`webhook-event-${kind}`}
                checked={events.includes(kind)}
                onChange={(event) => {
                  setEvents((current) =>
                    event.target.checked
                      ? [...current, kind]
                      : current.filter((one) => one !== kind),
                  );
                }}
              />{" "}
              {eventLabel(kind)}
            </label>
          ))}
        </fieldset>
      </ServerField>
      {events.length === 0 && (
        <p className="q-note" data-testid="webhook-events-needed">
          {copy.webhookEventsNeeded}
        </p>
      )}
      <label className="q-check">
        <ServerField field="active">
          <input
            type="checkbox"
            id="webhook-active"
            data-testid="webhook-active"
            checked={active}
            onChange={(event) => {
              setActive(event.target.checked);
            }}
          />
        </ServerField>{" "}
        {copy.webhookActiveLabel}
      </label>
      <div className="q-actions-row">
        <button
          type="button"
          className="q-button q-mini--primary"
          data-testid="webhook-save"
          disabled={busy || !ready}
          onClick={() => {
            void send();
          }}
        >
          {copy.webhookSave}
        </button>
        <button type="button" className="q-button q-button--quiet" onClick={onClose}>
          {strings.common.cancel}
        </button>
      </div>
    </ActionDialog>
  );
}
